/**
 * Seller-registration feature API layer + i18n namespace registration.
 *
 * Mirrors the `features/onboarding/api.ts` pattern:
 *   1. Registers `seller-registration` i18n namespace (ru/ky/en) on
 *      the shared i18next singleton via `addResourceBundle`.
 *   2. Exposes typed API call functions wrapping `apiClient`.
 *   3. Defines the TypeScript shapes for all request/response bodies,
 *      mirroring the backend Pydantic schemas in
 *      `apps/api/garden/features/seller_applications/{public_router,schemas}.py`.
 */
import i18n from "@/lib/i18n";
import { apiClient } from "@/lib/api-client";

import sellerRegEn from "@/locales/en/seller-registration.json";
import sellerRegKy from "@/locales/ky/seller-registration.json";
import sellerRegRu from "@/locales/ru/seller-registration.json";
import sellerEn from "@/locales/en/seller.json";
import sellerKy from "@/locales/ky/seller.json";
import sellerRu from "@/locales/ru/seller.json";

export const SELLER_REG_NS = "seller-registration";
export const SELLER_NS = "seller";

let sellerRegI18nRegistered = false;
let sellerI18nRegistered = false;

/**
 * Register the `seller-registration` i18n namespace. Idempotent + init-safe.
 * Called at the top of every seller-registration screen render.
 */
export function registerSellerRegI18n(): void {
  if (!i18n.isInitialized || typeof i18n.addResourceBundle !== "function") {
    sellerRegI18nRegistered = false;
    return;
  }
  if (sellerRegI18nRegistered) return;
  i18n.addResourceBundle("ru", SELLER_REG_NS, sellerRegRu, true, false);
  i18n.addResourceBundle("ky", SELLER_REG_NS, sellerRegKy, true, false);
  i18n.addResourceBundle("en", SELLER_REG_NS, sellerRegEn, true, false);
  sellerRegI18nRegistered = true;
}

/**
 * Register the `seller` i18n namespace. Idempotent + init-safe.
 * Called at the top of the ClaimPage render.
 */
export function registerSellerI18n(): void {
  if (!i18n.isInitialized || typeof i18n.addResourceBundle !== "function") {
    sellerI18nRegistered = false;
    return;
  }
  if (sellerI18nRegistered) return;
  i18n.addResourceBundle("ru", SELLER_NS, sellerRu, true, false);
  i18n.addResourceBundle("ky", SELLER_NS, sellerKy, true, false);
  i18n.addResourceBundle("en", SELLER_NS, sellerEn, true, false);
  sellerI18nRegistered = true;
}

// Best-effort eager registration for the production path.
registerSellerRegI18n();
registerSellerI18n();

// ─────────────────────────── Response types ────────────────────────────────

export interface RegisterStartResponse {
  application_id: string;
  expires_at: string;
}

export interface StepAckResponse {
  ok: boolean;
  next: string;
}

export interface OtpSendResponse {
  ok: boolean;
  retry_after_seconds: number;
}

export interface SubmitResponse {
  ok: boolean;
  status: string;
  estimated_review_hours: number;
}

export interface RegisterResumeResponse {
  application_id: string;
  status: string;
  current_step: string;
  profile: {
    business_name?: string | null;
    market_slug?: string | null;
    pavilion_number?: string | null;
    entrance_hint?: string | null;
    phone_e164?: string | null;
    whatsapp_phone_e164?: string | null;
    telegram_handle?: string | null;
  };
  email_verified: boolean;
  phone_verified: boolean;
  photo_uploaded: boolean;
}

export interface PublicMarketListItem {
  id: string;
  slug: string;
  name_ru: string;
  name_ky?: string | null;
}

export interface PublicMarketListResponse {
  items: PublicMarketListItem[];
}

// ─────────────────────────── API functions ─────────────────────────────────

/** Supported OTP locales (mirrors backend `RegisterStartRequest.locale`). */
type SellerRegLocale = "ru" | "ky" | "en";

/** Clamp the active i18n language to a supported locale; default `ru`. */
function currentSellerRegLocale(): SellerRegLocale {
  const lang = i18n.language;
  if (lang === "ky" || lang === "en") return lang;
  return "ru";
}

/** POST /sellers/register/start — create a new application. */
export function apiRegisterStart(email: string): Promise<RegisterStartResponse> {
  return apiClient.post<
    RegisterStartResponse,
    { email: string; locale: SellerRegLocale }
  >("/sellers/register/start", { email, locale: currentSellerRegLocale() });
}

/** POST /sellers/register/{id}/profile — save profile details. */
export function apiRegisterProfile(
  applicationId: string,
  body: {
    business_name: string;
    market_slug: string;
    pavilion_number: string;
    entrance_hint: string;
    phone_e164: string;
    whatsapp_phone_e164?: string | null;
    telegram_handle?: string | null;
  },
): Promise<StepAckResponse> {
  return apiClient.post<StepAckResponse, typeof body>(
    `/sellers/register/${applicationId}/profile`,
    body,
  );
}

/** POST /sellers/register/{id}/email/send — send email OTP. */
export function apiRegisterEmailSend(applicationId: string): Promise<OtpSendResponse> {
  return apiClient.post<OtpSendResponse, Record<string, never>>(
    `/sellers/register/${applicationId}/email/send`,
    {},
  );
}

/** POST /sellers/register/{id}/email/verify — verify email OTP. */
export function apiRegisterEmailVerify(
  applicationId: string,
  otp: string,
): Promise<StepAckResponse> {
  return apiClient.post<StepAckResponse, { otp: string }>(
    `/sellers/register/${applicationId}/email/verify`,
    { otp },
  );
}

/** POST /sellers/register/{id}/phone/send — send phone OTP. */
export function apiRegisterPhoneSend(applicationId: string): Promise<OtpSendResponse> {
  return apiClient.post<OtpSendResponse, Record<string, never>>(
    `/sellers/register/${applicationId}/phone/send`,
    {},
  );
}

/** POST /sellers/register/{id}/phone/verify — verify phone OTP. */
export function apiRegisterPhoneVerify(
  applicationId: string,
  otp: string,
): Promise<StepAckResponse> {
  return apiClient.post<StepAckResponse, { otp: string }>(
    `/sellers/register/${applicationId}/phone/verify`,
    { otp },
  );
}

/** POST /sellers/register/{id}/submit — submit the application. */
export function apiRegisterSubmit(applicationId: string): Promise<SubmitResponse> {
  return apiClient.post<SubmitResponse, Record<string, never>>(
    `/sellers/register/${applicationId}/submit`,
    {},
  );
}

/** GET /sellers/register/{id} — resume (hydrate wizard). */
export function apiRegisterResume(applicationId: string): Promise<RegisterResumeResponse> {
  return apiClient.get<RegisterResumeResponse>(`/sellers/register/${applicationId}`);
}

/** GET /marketplace/markets — public list for the market dropdown. */
export function apiFetchMarkets(): Promise<PublicMarketListResponse> {
  return apiClient.get<PublicMarketListResponse>("/marketplace/markets");
}

/** POST /sellers/claim — claim a seller account with an approval token. */
export function apiSellerClaim(token: string): Promise<{ seller_id: string }> {
  return apiClient.post<{ seller_id: string }, { token: string }>("/sellers/claim", {
    token,
  });
}
